/**
 * ADR 0418 D6 — the ONE place that decides what the page shows for this hostname, and the
 * only place allowed to decide "redirect to the marketing site".
 * plan: 4-Archives/tms/deeptrack-plan/0418-one-web-brand-by-hostname.md (mockup frames 1 · 2 · 4 · 5)
 *
 * Pure: the caller fetches `GET /public/brand?host=` and reads the cache; this file only judges.
 *
 * Rules that must not break (each is a row in brand-state.test.ts):
 * - **Redirect has exactly one road in**: a 404 whose body says `HOST_NOT_FOUND` with a safe
 *   `redirect_to`, on a host that is not in `NO_REDIRECT_HOSTS`. Network errors, 5xx, 429, a 404
 *   from something that is not our endpoint, a malformed body — none of them redirect. Getting
 *   this wrong sends real CCA staff to an advert the moment the API hiccups.
 * - **Nothing from the network reaches the page unchecked**: text is length-capped; logo,
 *   favicon and redirect URLs must be plain `https:` URLs without credentials — a `javascript:`
 *   or `data:` URL in an `<img>`/`<link>`/`location.replace` is how an injected value turns into
 *   script. The cached copy is checked the same way (anything can write to localStorage).
 */
import {
  BRAND_ERROR,
  BRAND_TEXT_MAX_LENGTH,
  BRAND_URL_MAX_LENGTH,
  isNoRedirectHost,
  type PublicBrand,
} from "@9nerd/shared";

/** What the fetch produced, as far as this decision cares. */
export type BrandResponse =
  | { status: "pending" }
  | { status: "network-error" }
  | { status: "http"; httpStatus: number; body: unknown };

export type BrandState =
  /** Frame 1 — no answer yet and nothing cached: reserve the space, show no name. */
  | { kind: "loading" }
  /** Frame 2 (fresh) · frame 5 with a cache (`stale: true`). */
  | { kind: "brand"; brand: PublicBrand; stale: boolean }
  /** Frame 4 — the API says this host belongs to nobody. */
  | { kind: "redirect"; to: string }
  /**
   * Frame 5 without a cache · plain localhost — no org. `platformName` (D17) is the neutral name
   * the API sent with its 404/400 (env PLATFORM_NAME); absent when the API did not answer.
   */
  | { kind: "neutral"; platformName?: string };

export interface BrandStateInput {
  /** Already `normalizeHost`-ed by the caller. */
  host: string;
  response: BrandResponse;
  /** Raw value read from localStorage under `brandCacheKey(host)`, or null. Untrusted. */
  cached: string | null;
}

export function resolveBrandState({ host, response, cached }: BrandStateInput): BrandState {
  const fromCache = parseCachedBrand(cached);
  const platformName = response.status === "http" ? platformNameOf(response.body) : null;
  const neutral = (): BrandState => (platformName ? { kind: "neutral", platformName } : { kind: "neutral" });
  const fallback = (): BrandState =>
    fromCache ? { kind: "brand", brand: fromCache, stale: true } : neutral();

  if (response.status === "pending") {
    return fromCache ? { kind: "brand", brand: fromCache, stale: true } : { kind: "loading" };
  }
  if (response.status === "network-error") return fallback();

  if (response.httpStatus === 200) {
    const brand = toPublicBrand(response.body);
    return brand ? { kind: "brand", brand, stale: false } : fallback();
  }

  if (response.httpStatus === 404) {
    const to = unknownHostRedirect(response.body);
    if (to && !isNoRedirectHost(host)) return { kind: "redirect", to };
    // A 404 we did not write (proxy, wrong API version) or an exempt dev host: not a verdict.
    return to ? neutral() : fallback();
  }

  // 400 / 429 / 5xx / anything else: the API did not say "unknown host", so do not act on it.
  return fallback();
}

/** The value to store under `brandCacheKey(host)` after a fresh 200. */
export function serializeBrandForCache(brand: PublicBrand): string {
  return JSON.stringify(brand);
}

// ───────────────────────────── validation ─────────────────────────────

function parseCachedBrand(raw: string | null): PublicBrand | null {
  if (raw === null || raw.length > 16_384) return null;
  try {
    return toPublicBrand(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** A `PublicBrand` built only from known, checked fields — or null if any required one is bad. */
export function toPublicBrand(body: unknown): PublicBrand | null {
  if (!isRecord(body)) return null;
  const org_slug = text(body.org_slug);
  const org_name = text(body.org_name);
  const login_title = text(body.login_title);
  const browser_title = text(body.browser_title);
  if (!org_slug || !org_name || !login_title || !browser_title) return null;
  const logo_url = optionalHttpsUrl(body.logo_url);
  const favicon_url = optionalHttpsUrl(body.favicon_url);
  if (logo_url === undefined || favicon_url === undefined) return null;
  // ADR 0421 D3 — behaviour flags: not a boolean → null (= bundle default), the brand is still good.
  // Unlike an unsafe image URL there is nothing to reject: every value has a safe fallback. An old API
  // (or an old cached brand) without these fields lands here as null too.
  return { org_slug, org_name, login_title, browser_title, logo_url, favicon_url, login_otp: flag(body.login_otp), show_powered_by: flag(body.show_powered_by) };
}

function flag(v: unknown): boolean | null {
  return typeof v === "boolean" ? v : null;
}

/** D17 — the API's neutral name, checked like any other display text. */
function platformNameOf(body: unknown): string | null {
  return isRecord(body) ? text(body.platform_name) : null;
}

function unknownHostRedirect(body: unknown): string | null {
  if (!isRecord(body) || body.error !== BRAND_ERROR.HOST_NOT_FOUND) return null;
  return safeHttpsUrl(body.redirect_to);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function text(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (t.length === 0 || t.length > BRAND_TEXT_MAX_LENGTH) return null;
  return t;
}

/** `null` = no image (fine) · `undefined` = present but unsafe (reject the whole brand). */
function optionalHttpsUrl(v: unknown): string | null | undefined {
  if (v === null || v === undefined) return null;
  return safeHttpsUrl(v) ?? undefined;
}

function safeHttpsUrl(v: unknown): string | null {
  if (typeof v !== "string" || v.length === 0 || v.length > BRAND_URL_MAX_LENGTH) return null;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || u.username !== "" || u.password !== "") return null;
  return u.toString();
}
